import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useSpamSchutz, Honigtopf } from "../components/SpamSchutz";
import { 
  CheckCircle, 
  Download, 
  Users, 
  Brain, 
  Briefcase,
  Building,
  Star,
  ArrowRight,
  FileText,
  Lightbulb
} from 'lucide-react';

const KIOrganisationWhitepaper = () => {
  const { t } = useLanguage();
  const { honigtopf, spamFelder } = useSpamSchutz();
  const [selectedWhitepapers, setSelectedWhitepapers] = useState([]);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const whitepapers = [
    {
      id: 'ki-organisation',
      title: t('org.whitepaper.organisation.title'),
      description: t('org.whitepaper.organisation.description'),
      icon: Building,
      color: 'bg-blue-500'
    },
    {
      id: 'human-in-loop',
      title: t('org.whitepaper.humanloop.title'),
      description: t('org.whitepaper.humanloop.description'),
      icon: Users,
      color: 'bg-green-500'
    },
    {
      id: 'ki-arbeitsplatz',
      title: t('org.whitepaper.workplace.title'),
      description: t('org.whitepaper.workplace.description'),
      icon: Briefcase,
      color: 'bg-purple-500'
    }
  ];

  const handleWhitepaperSelection = (whitepaperIs) => {
    setSelectedWhitepapers(prev => {
      if (prev.includes(whitepaperIs)) {
        return prev.filter(id => id !== whitepaperIs);
      } else {
        return [...prev, whitepaperIs];
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = t('org.form.errors.firstName');
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('org.form.errors.lastName');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('org.form.errors.email');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('org.form.errors.emailInvalid');
    }
    if (selectedWhitepapers.length === 0) {
      newErrors.whitepapers = t('org.form.errors.whitepapers');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL}/api/org-whitepaper-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(await spamFelder()),
          ...formData,
          selectedWhitepapers,
          pageSource: 'KI Organisation Whitepaper Landing',
          buttonContext: 'KI Organisation Whitepaper Download'
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ submit: t('org.form.errors.submit') });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead 
        title={t('org.seo.title')}
        description={t('org.seo.description')}
        keywords={t('org.seo.keywords')}
        canonicalUrl="/ki-organisation-whitepaper"
      />
      <div className="theme-brand min-h-screen bg-surface-sunken text-ink">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className=" text-white mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-900/20 border border-blue-800/50 rounded-full px-4 py-2 mb-6">
                <Star className="h-4 w-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-200">{t('org.hero.badge')}</span>
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                {t('org.hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-ink-muted max-w-4xl mx-auto mb-8 leading-relaxed">
                {t('org.hero.subtitle')}
              </p>

              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
                <div className="flex items-center gap-3 justify-center md:justify-start">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-ink-muted">{t('org.hero.benefit1')}</span>
                </div>
                <div className="flex items-center gap-3 justify-center">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-ink-muted">{t('org.hero.benefit2')}</span>
                </div>
                <div className="flex items-center gap-3 justify-center md:justify-end">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                  <span className="text-ink-muted">{t('org.hero.benefit3')}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Problem Statement */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="max-w-[64ch] mb-12">
              <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
                {t('org.problem.title')}
              </h2>
              <p className="text-lg text-ink-muted max-w-4xl mx-auto leading-relaxed">
                {t('org.problem.description')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <div className="bg-red-500/10 border border-red-400/25 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-red-300 mb-4">{t('org.problem.challenges.title')}</h3>
                <ul className="space-y-3">
                  <li className="text-red-300 flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    {t('org.problem.challenges.point1')}
                  </li>
                  <li className="text-red-300 flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    {t('org.problem.challenges.point2')}
                  </li>
                  <li className="text-red-300 flex items-start gap-2">
                    <span className="text-red-400 mt-1">•</span>
                    {t('org.problem.challenges.point3')}
                  </li>
                </ul>
              </div>

              <div className="bg-green-500/10 border border-green-400/25 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-green-300 mb-4">{t('org.problem.solution.title')}</h3>
                <p className="text-green-300 mb-4">{t('org.problem.solution.description')}</p>
                <ul className="space-y-3">
                  <li className="text-green-300 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-1" />
                    {t('org.problem.solution.point1')}
                  </li>
                  <li className="text-green-300 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-1" />
                    {t('org.problem.solution.point2')}
                  </li>
                  <li className="text-green-300 flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-green-400 mt-1" />
                    {t('org.problem.solution.point3')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Whitepaper Selection */}
        <section className="py-20 md:py-28 bg-surface-sunken">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="max-w-[64ch] mb-12">
              <h2 className="text-[clamp(2.1rem,4vw,3.4rem)] font-extrabold tracking-[-0.035em] leading-[1.05] [text-wrap:balance] text-ink mb-6">
                {t('org.selection.title')}
              </h2>
              <p className="text-lg text-ink-muted max-w-4xl mx-auto">
                {t('org.selection.subtitle')}
              </p>
            </div>

            <div className="space-y-4 mb-12">
              {whitepapers.map((wp) => {
                const IconComponent = wp.icon;
                const isSelected = selectedWhitepapers.includes(wp.id);
                
                return (
                  <Card 
                    key={wp.id} 
                    className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                      isSelected 
                        ? 'ring-2 ring-blue-500 bg-blue-500/10 border-blue-400/25' 
                        : 'hover:border-blue-300'
                    }`}
                    onClick={() => handleWhitepaperSelection(wp.id)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start gap-6">
                        {/* Großes, sichtbares Checkbox */}
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 ${
                          isSelected 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-hairline-strong bg-surface'
                        }`}>
                          {isSelected && <CheckCircle className="h-4 w-4 text-white" />}
                        </div>
                        
                        {/* Icon */}
                        <div className={`p-3 rounded-lg ${wp.color} text-white flex-shrink-0`}>
                          <IconComponent className="h-6 w-6" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-[1.28rem] tracking-[-0.02em] leading-snug text-ink mb-3">
                            {wp.title}
                          </h3>
                          <p className="text-ink-muted leading-relaxed">
                            {wp.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {errors.whitepapers && (
              <div className="text-red-400 text-sm mb-4 text-center">
                {errors.whitepapers}
              </div>
            )}
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-20 md:py-28 bg-surface">
          <div className="container mx-auto px-4 max-w-2xl">
            <div className="max-w-[64ch] mb-8">
              <h2 className="text-[clamp(1.9rem,3.6vw,3rem)] font-extrabold tracking-[-0.032em] leading-[1.06] [text-wrap:balance] text-ink mb-4">
                {t('org.form.title')}
              </h2>
              <p className="text-ink-muted">
                {t('org.form.subtitle')}
              </p>
            </div>

            {isSubmitted ? (
              <div className="bg-green-500/10 border border-green-400/25 rounded-xl p-8 text-center">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-green-300 mb-2">
                  {t('org.form.success.title')}
                </h3>
                <p className="text-green-300">
                  {t('org.form.success.message')}
                </p>
              </div>
            ) : (
              <Card>
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <Honigtopf {...honigtopf} />
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-2">
                          {t('org.form.firstName')} *
                        </label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.firstName ? 'border-red-400' : 'border-hairline-strong'
                          }`}
                          placeholder={t('org.form.firstName')}
                        />
                        {errors.firstName && (
                          <p className="text-red-400 text-sm mt-1">{errors.firstName}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-ink-muted mb-2">
                          {t('org.form.lastName')} *
                        </label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                            errors.lastName ? 'border-red-400' : 'border-hairline-strong'
                          }`}
                          placeholder={t('org.form.lastName')}
                        />
                        {errors.lastName && (
                          <p className="text-red-400 text-sm mt-1">{errors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('org.form.email')} *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.email ? 'border-red-400' : 'border-hairline-strong'
                        }`}
                        placeholder={t('org.form.email')}
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-ink-muted mb-2">
                        {t('org.form.phone')}
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-hairline-strong rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder={t('org.form.phone')}
                      />
                    </div>

                    {selectedWhitepapers.length > 0 && (
                      <div className="bg-blue-500/10 rounded-lg p-4">
                        <h4 className="font-medium text-blue-200 mb-2">
                          {t('org.form.selectedWhitepapers')}:
                        </h4>
                        <ul className="space-y-1">
                          {selectedWhitepapers.map(wpId => {
                            const wp = whitepapers.find(w => w.id === wpId);
                            return (
                              <li key={wpId} className="text-sm text-blue-300 flex items-center gap-2">
                                <FileText className="h-3 w-3" />
                                {wp?.title}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {errors.submit && (
                      <div className="text-red-400 text-sm text-center">
                        {errors.submit}
                      </div>
                    )}

                    <Button 
                      type="submit" 
                      disabled={isSubmitting || selectedWhitepapers.length === 0}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        t('org.form.submitting')
                      ) : (
                        <>
                          <Download className="h-5 w-5 mr-2" />
                          {t('org.form.submit')}
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-ink-muted text-center">
                      {t('org.form.privacy')}
                    </p>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default KIOrganisationWhitepaper;